import type { IStudioBreakdownExcelConfig, TBreakdownDataCol } from './types';
import {
  buildDataCols,
  collectSortedMonths,
  formatCellValue,
  formatMonthLabel,
  groupMonthsByYear,
  sumValues,
} from './monthColumns';
import { exportCsvRows } from '../exportTableToCsv';
import { resolveStudioBreakdownBlocks } from './resolveStudioBlocks';

const INFO_COL_COUNT = 4;

const emptyInfoCells = (): string[] => Array(INFO_COL_COUNT).fill('');

const buildYearLabelCells = (dataCols: TBreakdownDataCol[]): string[] => (
  dataCols.map((col) => (col.type === 'month' ? col.month.slice(0, 4) : col.year))
);

/**
 * CSV mirrors the Excel breakdown layout (notes → years → Update Time → headers → data).
 * Colors/bold cannot be expressed in CSV — structure and values match xlsx only.
 */
export const exportStudioBreakdownCsv = <TMonthRow>(
  config: IStudioBreakdownExcelConfig<TMonthRow>,
): void => {
  const {
    name,
    noteRows,
    unitLabel,
    infoHeaders,
    datatime,
    getMonthKey,
    isActualMonth,
  } = config;

  const studioBlocks = resolveStudioBreakdownBlocks(config);
  const allMonthRows = studioBlocks.flatMap((block) => block.rawData);
  const sortedMonths = collectSortedMonths(allMonthRows, getMonthKey);
  const yearMap = groupMonthsByYear(sortedMonths);
  const dataCols = buildDataCols(yearMap);

  const resolveMonthRow = (month: string): TMonthRow | undefined => {
    for (const block of studioBlocks) {
      const match = block.rawData.find((row) => getMonthKey(row) === month);
      if (match) {
        return match;
      }
    }
    return undefined;
  };

  const getYearActualStatus = (year: string): boolean | null => {
    const yearMonths = yearMap.get(year) ?? [];
    if (!yearMonths.length) return null;
    const statuses = yearMonths.map((month) => {
      const monthRow = resolveMonthRow(month);
      return monthRow ? isActualMonth(monthRow) : null;
    });
    if (statuses.some((status) => status === null)) return null;
    return statuses.every((status) => status === true);
  };

  const actualForecastLabels = dataCols.map((col) => {
    if (col.type === 'month') {
      const monthRow = resolveMonthRow(col.month);
      const status = monthRow ? isActualMonth(monthRow) : null;
      if (status === null) return '';
      return status ? 'Actual' : 'Forecast';
    }
    const status = getYearActualStatus(col.year);
    if (status === null) return '';
    return status ? 'Actual' : 'Forecast';
  });

  const rows: unknown[][] = [];
  const hasTrailingYears = noteRows.some((note) => note.trailingYears);

  // Same preamble order as exportStudioBreakdownExcel.
  noteRows.forEach((note) => {
    const noteRow = [...emptyInfoCells()];
    noteRow[0] = note.text;
    if (note.trailingYears) {
      rows.push([...noteRow, ...buildYearLabelCells(dataCols)]);
      return;
    }
    rows.push(noteRow);
  });

  // Dedicated year row when notes do not carry trailingYears (e.g. Burn Rate).
  if (!hasTrailingYears) {
    rows.push([...emptyInfoCells(), ...buildYearLabelCells(dataCols)]);
  }

  const formattedDatatime = datatime ? datatime.replace('-', '/') : '';
  const metaRow = [...emptyInfoCells()];
  if (unitLabel) {
    metaRow[0] = unitLabel;
  }
  // Update Time sits in column D — same position as the Excel template.
  metaRow[INFO_COL_COUNT - 1] = `Update Time: ${formattedDatatime}`;
  rows.push([...metaRow, ...actualForecastLabels]);

  rows.push([
    ...infoHeaders,
    ...dataCols.map((col) => (
      col.type === 'month' ? formatMonthLabel(col.month) : 'Total'
    )),
  ]);

  studioBlocks.forEach((block) => {
    const monthLookup = new Map<string, TMonthRow>();
    block.rawData.forEach((row) => monthLookup.set(getMonthKey(row), row));

    block.fixedRows.forEach((fixedRow) => {
      const dataCells = dataCols.map((col) => {
        if (col.type === 'total') {
          const yearMonths = yearMap.get(col.year) ?? [];
          const monthValues = yearMonths
            .map((m) => monthLookup.get(m))
            .filter((apiRow): apiRow is TMonthRow => !!apiRow)
            .map((apiRow) => fixedRow.getValue(apiRow));
          return formatCellValue(sumValues(...monthValues));
        }
        const apiRow = monthLookup.get(col.month);
        return apiRow ? formatCellValue(fixedRow.getValue(apiRow)) : '';
      });

      rows.push([
        block.studioName,
        fixedRow.level1,
        fixedRow.level2 || '',
        fixedRow.level3 || '',
        ...dataCells,
      ]);
    });
  });

  exportCsvRows({ fileName: name, rows });
};
